import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Archive,
  ArchiveRestore,
  ArrowLeft,
  ArrowRight,
  Bot,
  Briefcase,
  CheckCircle2,
  Clock3,
  Code2,
  Component,
  FileSpreadsheet,
  FileText,
  KeyRound,
  Layers3,
  LoaderCircle,
  MousePointerClick,
  Pencil,
  Plus,
  RefreshCw,
  Search,
  Settings2,
  ShieldCheck,
  Trash2,
  Type,
  Users,
  X,
} from 'lucide-react';
import { ApplicationPomManager } from './ApplicationPomManager';
import { API_BASE_URL, API_ENDPOINTS } from '../../constants/api';
import { apiFetch } from '../../utils/apiClient';
import {
  applicationEnvironmentPreferenceUserId,
  getActiveApplicationEnvironmentId,
  setActiveApplicationEnvironmentId,
} from '../../utils/applicationEnvironmentPreference';
import {
  APPLICATION_MODULE_ORDER,
  APPLICATION_MODULES,
  buildApplicationModulePath,
  buildApplicationPath,
  buildApplicationPomsPath,
  buildApplicationSectionsPath,
  buildApplicationSourceOpenPath,
  buildApplicationSourcePath,
  buildApplicationsPath,
  buildNewApplicationSourcePath,
  normalizeApplicationModule,
  parseApplicationRoute,
} from '../../utils/applicationRoutes';
import { PDFWorkflow } from '../pdf/PDFWorkflow';
import { GeneratePOMStep } from '../pdf/GeneratePOMStep';
import { FeatureCaptureWorkspace } from '../featureCapture/FeatureCaptureWorkspace';
import RecordedFlowBuilder from '../featureCapture/RecordedFlowBuilder';
import { WorkspaceMembersModal } from '../modals/WorkspaceMembersModal';
import { AutomationEnvironmentModal } from '../modals/AutomationEnvironmentModal';
import { ApplicationJobCenter, ApplicationJobIndicator } from '../jobs/ApplicationJobCenter';

const MODULE_ICONS = {
  brd: FileText,
  requirements: Type,
  api: Code2,
  excel: FileSpreadsheet,
  feature: MousePointerClick,
};

const MODULE_TONES = {
  brd: 'application-tone-brd',
  requirements: 'application-tone-requirements',
  api: 'application-tone-api',
  excel: 'application-tone-excel',
  feature: 'application-tone-feature',
};

const parseApiError = async (response, fallback) => {
  try {
    const payload = await response.json();
    return payload?.detail || payload?.message || fallback;
  } catch (_err) {
    return fallback;
  }
};

const formatDate = (value) => {
  const date = value ? new Date(value) : null;
  if (!date || Number.isNaN(date.getTime())) return 'Not updated';
  return date.toLocaleString([], {
    month: 'short',
    day: 'numeric',
    year: date.getFullYear() === new Date().getFullYear() ? undefined : 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatStatus = (value) => {
  const normalized = String(value || '').trim().replace(/[_-]+/g, ' ');
  if (!normalized) return 'Ready';
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
};

const compactCount = (value) => {
  const count = Number(value);
  return Number.isFinite(count) ? Math.max(0, count) : 0;
};

const sanitizeInternalReturnPath = (value = '') => {
  const normalized = String(value || '').trim();
  if (!normalized || !normalized.startsWith('/') || normalized.startsWith('//')) return '';
  return normalized.replace(/[\r\n]/g, '');
};

const buildReturnToSearch = (returnTo = '') => {
  const normalized = sanitizeInternalReturnPath(returnTo);
  if (!normalized) return '';
  const params = new URLSearchParams();
  params.set('returnTo', normalized);
  return `?${params.toString()}`;
};

const subtractCount = (value, amount = 1) => (
  Math.max(0, compactCount(value) - compactCount(amount))
);

const sourceCount = (source, key) => compactCount(source?.counts?.[key]);

const removeSourceFromOverview = (overview, source, fallbackModuleId = '') => {
  if (!overview || !source) return overview;
  const sourceId = String(source?.id || '').trim();
  const sourceType = normalizeApplicationModule(source?.source_type || fallbackModuleId);
  const summary = overview.summary || {};
  const total = summary.total || {};
  const byType = summary.by_type || {};
  const typeSummary = sourceType ? (byType[sourceType] || {}) : {};

  return {
    ...overview,
    summary: {
      ...summary,
      total: {
        ...total,
        sources: subtractCount(total.sources, 1),
        test_cases: subtractCount(total.test_cases, sourceCount(source, 'test_cases')),
        scripts: subtractCount(total.scripts, sourceCount(source, 'scripts')),
        runs: subtractCount(total.runs, sourceCount(source, 'runs')),
      },
      by_type: sourceType
        ? {
          ...byType,
          [sourceType]: {
            ...typeSummary,
            sources: subtractCount(typeSummary.sources, 1),
            test_cases: subtractCount(typeSummary.test_cases, sourceCount(source, 'test_cases')),
            poms: subtractCount(typeSummary.poms, sourceCount(source, 'poms')),
            scripts: subtractCount(typeSummary.scripts, sourceCount(source, 'scripts')),
            runs: subtractCount(typeSummary.runs, sourceCount(source, 'runs')),
          },
        }
        : byType,
    },
    recent_sources: Array.isArray(overview.recent_sources)
      ? overview.recent_sources.filter((item) => String(item?.id || '').trim() !== sourceId)
      : overview.recent_sources,
  };
};

const ApplicationPageSkeleton = () => (
  <div className="application-portal-skeleton" role="status" aria-label="Loading application">
    <div className="application-skeleton-sidebar">
      <div className="application-skeleton-block h-12" />
      {[0, 1, 2, 3, 4, 5].map((item) => (
        <div key={item} className="application-skeleton-block h-10" />
      ))}
    </div>
    <div className="application-skeleton-main">
      <div className="application-skeleton-block h-20" />
      <div className="grid gap-3 md:grid-cols-4">
        {[0, 1, 2, 3].map((item) => (
          <div key={item} className="application-skeleton-block h-24" />
        ))}
      </div>
      <div className="application-skeleton-block h-80" />
    </div>
  </div>
);

const ApplicationContentSkeleton = () => (
  <div className="application-content-skeleton" role="status" aria-label="Loading application dashboard">
    <div className="application-skeleton-block h-20" />
    <div className="application-overview-metrics">
      {[0, 1, 2, 3].map((item) => (
        <div key={item} className="application-skeleton-block h-24" />
      ))}
    </div>
    <div className="application-skeleton-block h-72" />
  </div>
);

const EmptyState = ({ icon: Icon, title, copy, action = null }) => (
  <div className="application-empty-state">
    <span className="application-empty-icon" aria-hidden="true">
      <Icon className="h-6 w-6" />
    </span>
    <h3>{title}</h3>
    <p>{copy}</p>
    {action}
  </div>
);

const ApplicationShell = ({
  application,
  route,
  overview,
  onNavigate,
  onMembers,
  children,
}) => {
  const [jobCenterOpen, setJobCenterOpen] = useState(false);
  const allowedModules = new Set(
    Array.isArray(application?.current_user_module_access)
      ? application.current_user_module_access.map(normalizeApplicationModule).filter(Boolean)
      : APPLICATION_MODULE_ORDER
  );
  return (
    <main className="application-shell">
      <aside className="application-sidebar">
        <button
          type="button"
          className="application-sidebar-app"
          onClick={() => onNavigate(buildApplicationPath(application.id))}
        >
          <span className="application-sidebar-app-icon" aria-hidden="true">
            <Briefcase className="h-5 w-5" />
          </span>
          <span className="min-w-0">
            <small>Application</small>
            <strong title={application.name}>{application.name}</strong>
          </span>
        </button>

        <nav className="application-sidebar-nav" aria-label={`${application.name} navigation`}>
          <button
            type="button"
            className={!route.module ? 'is-active' : ''}
            onClick={() => onNavigate(buildApplicationPath(application.id))}
          >
            <Layers3 className="h-4 w-4" aria-hidden="true" />
            <span>Overview</span>
          </button>
          {APPLICATION_MODULE_ORDER.map((moduleId) => {
            const module = APPLICATION_MODULES[moduleId];
            const Icon = MODULE_ICONS[moduleId];
            const hasAccess = allowedModules.has(moduleId);
            if (!hasAccess) return null;
            return (
              <button
                key={moduleId}
                type="button"
                className={route.module === moduleId ? 'is-active' : ''}
                onClick={() => onNavigate(buildApplicationModulePath(application.id, moduleId))}
              >
                <Icon className="h-4 w-4" aria-hidden="true" />
                <span>{module.label}</span>
                <span className="application-sidebar-count">
                  {compactCount(overview?.summary?.by_type?.[moduleId]?.sources)}
                </span>
              </button>
            );
          })}
        </nav>

        <ApplicationJobIndicator
          applicationId={application.id}
          onOpen={() => setJobCenterOpen(true)}
        />

        <div className="application-sidebar-footer">
          <button
            type="button"
            className={route.kind === 'poms' ? 'is-active' : ''}
            onClick={() => onNavigate(buildApplicationPomsPath(application.id))}
          >
            <Component className="h-4 w-4" aria-hidden="true" />
            <span>Page Objects</span>
            <span className="application-sidebar-count">
              {compactCount(overview?.summary?.total?.poms)}
            </span>
          </button>
          <button type="button" onClick={onMembers}>
            <Users className="h-4 w-4" aria-hidden="true" />
            <span>Team</span>
            <span className="application-sidebar-count">
              {compactCount(overview?.members_count)}
            </span>
          </button>
          <button type="button" onClick={() => onNavigate('/applications')}>
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            <span>All applications</span>
          </button>
        </div>
      </aside>
      <section className="application-content">{children}</section>
      <ApplicationJobCenter
        applicationId={application.id}
        applicationName={application.name}
        open={jobCenterOpen}
        onClose={() => setJobCenterOpen(false)}
        onNavigate={onNavigate}
      />
    </main>
  );
};

const ApplicationsList = ({
  applications,
  loading,
  error,
  canCreate,
  createApplication,
  archiveApplication,
  restoreApplication,
  refresh,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const requestedModule = normalizeApplicationModule(
    new URLSearchParams(location.search || '').get('module')
  );
  const [search, setSearch] = useState('');
  const [creating, setCreating] = useState(false);
  const [archivingId, setArchivingId] = useState('');
  const [confirmArchiveId, setConfirmArchiveId] = useState('');
  const [newName, setNewName] = useState('');
  const [createError, setCreateError] = useState('');
  const [archiveError, setArchiveError] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [allApplications, setAllApplications] = useState([]);
  const [archivedLoading, setArchivedLoading] = useState(false);
  const [archivedError, setArchivedError] = useState('');

  const loadArchived = useCallback(async () => {
    setArchivedLoading(true);
    setArchivedError('');
    try {
      const response = await apiFetch(`${API_BASE_URL}${API_ENDPOINTS.APPLICATIONS}?include_archived=true`);
      if (!response.ok) throw new Error(await parseApiError(response, 'Failed to load archived applications.'));
      const payload = await response.json();
      setAllApplications(Array.isArray(payload?.applications) ? payload.applications : []);
    } catch (err) {
      setArchivedError(err?.message || 'Failed to load archived applications.');
    } finally {
      setArchivedLoading(false);
    }
  }, []);

  useEffect(() => {
    if (showArchived) loadArchived();
  }, [loadArchived, showArchived]);

  const visible = useMemo(() => {
    const query = String(search || '').trim().toLowerCase();
    const rows = showArchived ? allApplications : applications;
    if (!query) return rows;
    return rows.filter((application) => (
      String(application?.name || '').toLowerCase().includes(query)
    ));
  }, [allApplications, applications, search, showArchived]);

  const openApplication = (application) => {
    navigate(
      requestedModule
        ? buildApplicationModulePath(application.id, requestedModule)
        : buildApplicationPath(application.id)
    );
  };

  const handleCreate = async (event) => {
    event.preventDefault();
    if (!String(newName || '').trim()) return;
    setCreating(true);
    setCreateError('');
    try {
      const application = await createApplication({ name: newName });
      setNewName('');
      if (application?.id) openApplication(application);
    } catch (err) {
      setCreateError(err?.message || 'Failed to create application.');
    } finally {
      setCreating(false);
    }
  };

  const handleArchive = async (application) => {
    const applicationId = String(application?.id || '').trim();
    if (!applicationId || !archiveApplication) return;
    if (confirmArchiveId !== applicationId) {
      setConfirmArchiveId(applicationId);
      setArchiveError('');
      return;
    }
    setArchivingId(applicationId);
    setArchiveError('');
    try {
      await archiveApplication(application);
      if (showArchived) await loadArchived();
      setConfirmArchiveId('');
    } catch (err) {
      setArchiveError(err?.message || 'Failed to archive application.');
    } finally {
      setArchivingId('');
    }
  };

  const handleRestore = async (application) => {
    const applicationId = String(application?.id || '').trim();
    if (!applicationId || !restoreApplication) return;
    setArchivingId(applicationId);
    setArchiveError('');
    try {
      await restoreApplication(application);
      await loadArchived();
    } catch (err) {
      setArchiveError(err?.message || 'Failed to restore application.');
    } finally {
      setArchivingId('');
    }
  };

  return (
    <main className="applications-index-page">
      <header className="applications-index-header">
        <div>
          <p className="application-eyebrow">Product portfolio</p>
          <h1>Applications</h1>
          <p>Select the product you are testing, then choose how you want to test it.</p>
        </div>
        {canCreate && (
          <form className="application-create-inline" onSubmit={handleCreate}>
            <label className="sr-only" htmlFor="new-application-name">Application name</label>
            <input
              id="new-application-name"
              value={newName}
              onChange={(event) => setNewName(event.target.value)}
              placeholder="New application name"
              className="text-input"
              maxLength={120}
            />
            <button
              type="submit"
              className="primary-btn"
              disabled={creating || !String(newName || '').trim()}
            >
              {creating ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
              Create
            </button>
          </form>
        )}
      </header>

      <div className="applications-index-toolbar">
        <label className="application-search">
          <Search className="h-4 w-4" aria-hidden="true" />
          <input
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search applications"
            aria-label="Search applications"
          />
        </label>
        <label className="application-archive-toggle">
          <input
            type="checkbox"
            checked={showArchived}
            onChange={(event) => setShowArchived(event.target.checked)}
          />
          <span>Show archived</span>
        </label>
        <button type="button" className="icon-btn" onClick={() => { refresh(); if (showArchived) loadArchived(); }} disabled={loading || archivedLoading} title="Refresh">
          <RefreshCw className={`h-4 w-4 ${loading || archivedLoading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {(error || createError || archiveError || archivedError) && (
        <div className="application-error-banner">{createError || archiveError || archivedError || error}</div>
      )}

      {loading || (showArchived && archivedLoading) ? (
        <div className="applications-list-skeleton">
          {[0, 1, 2, 3].map((item) => (
            <div key={item} className="application-skeleton-block h-28" />
          ))}
        </div>
      ) : error ? (
        <EmptyState
          icon={RefreshCw}
          title="Applications could not load"
          copy="Refresh the list after the backend is available."
          action={(
            <button type="button" className="secondary-btn" onClick={refresh}>
              <RefreshCw className="h-4 w-4" />
              Retry
            </button>
          )}
        />
      ) : visible.length ? (
        <div className="applications-list">
          {visible.map((application) => {
            const isArchived = String(application?.status || '').toLowerCase() === 'archived';
            const moduleAccess = Array.isArray(application?.current_user_module_access)
              ? application.current_user_module_access.length
              : APPLICATION_MODULE_ORDER.length;
            const canArchive = Array.isArray(application?.current_user_permissions)
              && application.current_user_permissions.includes('application:manage');
            const isConfirmingArchive = confirmArchiveId === String(application?.id || '');
            const isArchiving = archivingId === String(application?.id || '');
            return (
              <div
                key={application.id}
                className="application-list-row"
              >
                <button
                  type="button"
                  className="application-list-open"
                  onClick={() => openApplication(application)}
                  disabled={isArchived}
                >
                  <span className="application-list-icon" aria-hidden="true">
                    <Briefcase className="h-5 w-5" />
                  </span>
                  <span className="application-list-copy">
                    <strong>{application.name}{isArchived && <em className="application-archived-badge">Archived</em>}</strong>
                    <span>
                      {formatStatus(application.current_user_role)} access
                      <span aria-hidden="true"> · </span>
                      {moduleAccess} testing area{moduleAccess === 1 ? '' : 's'}
                    </span>
                  </span>
                  <span className="application-list-meta">
                    <span>{formatDate(application.updated_at || application.created_at)}</span>
                    {!isArchived && <ArrowRight className="h-4 w-4" aria-hidden="true" />}
                  </span>
                </button>
                {canArchive && (
                  <button
                    type="button"
                    className={`application-list-archive ${isArchived ? 'is-restore' : ''} ${isConfirmingArchive ? 'is-confirming' : ''}`}
                    onClick={() => isArchived ? handleRestore(application) : handleArchive(application)}
                    disabled={isArchiving}
                    title={isArchived ? `Restore ${application.name}` : isConfirmingArchive ? `Confirm archive ${application.name}` : `Archive ${application.name}`}
                    aria-label={isArchived ? `Restore ${application.name}` : isConfirmingArchive ? `Confirm archive ${application.name}` : `Archive ${application.name}`}
                  >
                    {isArchiving ? <LoaderCircle className="h-4 w-4 animate-spin" /> : isArchived ? <ArchiveRestore className="h-4 w-4" /> : <Archive className="h-4 w-4" />}
                    {isConfirmingArchive && <span>Confirm</span>}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <EmptyState
          icon={Briefcase}
          title={search ? 'No matching applications' : 'No applications yet'}
          copy={search ? 'Try a different application name.' : 'Create an application to start organizing test coverage.'}
        />
      )}
    </main>
  );
};

const OverviewMetric = ({ icon: Icon, label, value, tone = '' }) => (
  <div className={`application-overview-metric ${tone}`}>
    <span aria-hidden="true"><Icon className="h-4 w-4" /></span>
    <div>
      <small>{label}</small>
      <strong>{compactCount(value)}</strong>
    </div>
  </div>
);

const ApplicationOverview = ({
  application,
  overview,
  onNavigate,
  onMembers,
  onSettings,
  onAddEnvironment,
  onCaptureEnvironmentLogin,
  onManageEnvironment,
  onArchiveEnvironment,
  activeEnvironmentId,
  onSelectEnvironment,
  canManage,
}) => {
  const summary = overview?.summary || {};
  const totals = summary.total || {};
  const environments = Array.isArray(overview?.environments) ? overview.environments : [];
  const recentSources = Array.isArray(overview?.recent_sources) ? overview.recent_sources : [];
  const allowedModules = new Set(
    Array.isArray(application?.current_user_module_access)
      ? application.current_user_module_access
      : APPLICATION_MODULE_ORDER
  );

  return (
    <>
      <header className="application-page-header">
        <div>
          <p className="application-eyebrow">Application dashboard</p>
          <h1>{application.name}</h1>
          <p>Testing coverage, automation assets, runs, and team activity in one place.</p>
        </div>
        <div className="application-page-actions">
          <button type="button" className="secondary-btn" onClick={onMembers}>
            <Users className="h-4 w-4" />
            Team
          </button>
          {canManage && (
            <button
              type="button"
              className="icon-btn"
              onClick={onSettings}
              title="Application settings"
              aria-label="Application settings"
            >
              <Settings2 className="h-4 w-4" />
            </button>
          )}
        </div>
      </header>

      <section className="application-overview-metrics" aria-label="Application totals">
        <OverviewMetric icon={Layers3} label="Sources" value={totals.sources} />
        <OverviewMetric icon={CheckCircle2} label="Test Cases" value={totals.test_cases} tone="is-green" />
        <OverviewMetric icon={Bot} label="Automation" value={totals.scripts} tone="is-blue" />
        <OverviewMetric icon={Component} label="POMs" value={totals.poms} tone="is-violet" />
      </section>

      <section className="application-section">
        <div className="application-section-heading">
          <div>
            <p className="application-eyebrow">Testing areas</p>
            <h2>Choose how to test this application</h2>
          </div>
        </div>
        <div className="application-module-grid">
          {APPLICATION_MODULE_ORDER.map((moduleId) => {
            if (!allowedModules.has(moduleId)) return null;
            const module = APPLICATION_MODULES[moduleId];
            const Icon = MODULE_ICONS[moduleId];
            const moduleSummary = summary?.by_type?.[moduleId] || {};
            return (
              <button
                type="button"
                key={moduleId}
                className={`application-module-card ${MODULE_TONES[moduleId]}`}
                onClick={() => onNavigate(buildApplicationModulePath(application.id, moduleId))}
              >
                <span className="application-module-icon" aria-hidden="true">
                  <Icon className="h-5 w-5" />
                </span>
                <span className="application-module-copy">
                  <strong>{module.label}</strong>
                  <span>{module.description}</span>
                </span>
                <span className="application-module-stat">
                  <strong>{compactCount(moduleSummary.sources)}</strong>
                  <small>source{compactCount(moduleSummary.sources) === 1 ? '' : 's'}</small>
                </span>
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </button>
            );
          })}
        </div>
      </section>

      <div className="application-overview-columns">
        <section className="application-section">
          <div className="application-section-heading">
            <div>
              <p className="application-eyebrow">Recent work</p>
              <h2>Continue testing</h2>
            </div>
          </div>
          {recentSources.length ? (
            <div className="application-compact-list">
              {recentSources.map((source) => {
                const moduleId = normalizeApplicationModule(source.source_type);
                const Icon = MODULE_ICONS[moduleId] || FileText;
                return (
                  <button
                    key={source.id}
                    type="button"
                    onClick={() => onNavigate(source.continue_route)}
                  >
                    <span className={`application-source-icon ${MODULE_TONES[moduleId]}`}>
                      <Icon className="h-4 w-4" />
                    </span>
                    <span className="min-w-0">
                      <strong>{source.title}</strong>
                      <small>{APPLICATION_MODULES[moduleId]?.label || 'Test source'} · {formatStatus(source.current_stage)}</small>
                    </span>
                    <span>{formatDate(source.updated_at)}</span>
                    <ArrowRight className="h-4 w-4" />
                  </button>
                );
              })}
            </div>
          ) : (
            <EmptyState
              icon={Layers3}
              title="No test sources yet"
              copy="Choose a testing area above to create the first source."
            />
          )}
        </section>

        <section className="application-section">
          <div className="application-section-heading">
            <div>
              <p className="application-eyebrow">Environments</p>
              <h2>Application environments</h2>
            </div>
            <div className="flex flex-wrap items-center justify-end gap-2">
              {canManage && (
                <button type="button" className="secondary-btn application-environment-add" onClick={onAddEnvironment}>
                  <Plus className="h-4 w-4" />
                  Add environment
                </button>
              )}
            </div>
          </div>
          <div className="application-environment-list">
            {environments.map((environment) => (
              <div key={environment.id} className={String(environment.id) === String(activeEnvironmentId) ? 'is-active' : ''}>
                <span className={environment.is_default ? 'is-default' : ''}>
                  <ShieldCheck className="h-4 w-4" />
                </span>
                <span className="min-w-0">
                  <strong>{environment.name}</strong>
                  <small title={environment.base_url}>
                    {environment.base_url || 'Application URL not configured'}
                  </small>
                </span>
                <span className="application-environment-actions">
                  {environment.is_default && <em>Default</em>}
                  {String(environment.id) === String(activeEnvironmentId) && <em className="is-active">Active for you</em>}
                  {String(environment.id) !== String(activeEnvironmentId) && (
                    <button
                      type="button"
                      className="application-environment-action"
                      onClick={() => onSelectEnvironment(environment.id)}
                    >
                      Use
                    </button>
                  )}
                  {environment.auth_source_id && (
                    <button
                      type="button"
                      className="application-environment-action"
                      onClick={() => onCaptureEnvironmentLogin(environment)}
                      aria-label={`Configure login for ${environment.name}`}
                    >
                      <KeyRound className="h-4 w-4" />
                      Login setup
                    </button>
                  )}
                  {canManage && (
                    <button
                      type="button"
                      className="application-environment-action"
                      onClick={() => onManageEnvironment(environment)}
                      aria-label={`Edit ${environment.name}`}
                    >
                      <Pencil className="h-4 w-4" />
                      Edit
                    </button>
                  )}
                  {canManage && !environment.is_default && (
                    <button
                      type="button"
                      className="application-environment-action is-icon is-danger"
                      onClick={() => onArchiveEnvironment(environment)}
                      aria-label={`Archive ${environment.name}`}
                      title={`Archive ${environment.name}`}
                    >
                      <Archive className="h-4 w-4" />
                    </button>
                  )}
                </span>
              </div>
            ))}
          </div>
        </section>
      </div>
    </>
  );
};

const ModuleDashboard = ({
  application,
  moduleId,
  sources,
  loading,
  error,
  canManage = false,
  onNavigate,
  onRefresh,
  onDeleteSource,
  onArchiveSource,
  showArchived = false,
  onToggleShowArchived,
}) => {
  const [query, setQuery] = useState('');
  const [confirmDeleteId, setConfirmDeleteId] = useState('');
  const [deletingId, setDeletingId] = useState('');
  const [archivingId, setArchivingId] = useState('');
  const [deleteError, setDeleteError] = useState('');
  const [showRecordedFlowBuilder, setShowRecordedFlowBuilder] = useState(false);
  const module = APPLICATION_MODULES[moduleId];
  const Icon = MODULE_ICONS[moduleId];
  const displayError = deleteError || error;
  const visibleSources = useMemo(() => {
    const normalized = String(query || '').trim().toLowerCase();
    if (!normalized) return sources;
    return sources.filter((source) => (
      [source.title, source.status, source.current_stage]
        .some((value) => String(value || '').toLowerCase().includes(normalized))
    ));
  }, [query, sources]);

  return (
    <>
      <header className="application-page-header">
        <div>
          <p className="application-eyebrow">{application.name}</p>
          <h1>{module.label}</h1>
          <p>{module.description}</p>
        </div>
        <div className="application-page-actions">
          <button type="button" className="icon-btn" onClick={onRefresh} disabled={loading} title="Refresh">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
          {moduleId === 'feature' && (
            <button
              type="button"
              className="secondary-btn application-new-source"
              onClick={() => setShowRecordedFlowBuilder(true)}
              disabled={loading}
            >
              <Component className="h-4 w-4" />
              Create flow from recordings
            </button>
          )}
          <button
            type="button"
            className={`primary-btn application-new-source ${MODULE_TONES[moduleId]}`}
            onClick={() => onNavigate(buildNewApplicationSourcePath(application.id, moduleId))}
          >
            <Plus className="h-4 w-4" />
            New {module.shortLabel}
          </button>
        </div>
      </header>

      <section className="application-module-summary">
        <span className={`application-module-summary-icon ${MODULE_TONES[moduleId]}`}>
          <Icon className="h-6 w-6" />
        </span>
        <div>
          <strong>{sources.length}</strong>
          <span>source{sources.length === 1 ? '' : 's'}</span>
        </div>
        <div>
          <strong>{sources.reduce((sum, source) => sum + compactCount(source?.counts?.test_cases), 0)}</strong>
          <span>test cases</span>
        </div>
        <div>
          <strong>{sources.reduce((sum, source) => sum + compactCount(source?.counts?.scripts), 0)}</strong>
          <span>scripts</span>
        </div>
        <div>
          <strong>{sources.reduce((sum, source) => sum + compactCount(source?.counts?.runs), 0)}</strong>
          <span>runs</span>
        </div>
      </section>

      <section className="application-section application-source-section">
        <div className="application-source-toolbar">
          <label className="application-search">
            <Search className="h-4 w-4" />
            <input
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={`Search ${module.label.toLowerCase()}`}
              aria-label={`Search ${module.label}`}
            />
          </label>
          <label className="application-archive-toggle">
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(event) => onToggleShowArchived?.(event.target.checked)}
            />
            <span>Show archived</span>
          </label>
          <span>{visibleSources.length} shown</span>
        </div>

        {displayError && <div className="application-error-banner">{displayError}</div>}

        {loading ? (
          <div className="application-source-skeleton">
            {[0, 1, 2, 3].map((item) => (
              <div key={item} className="application-skeleton-block h-20" />
            ))}
          </div>
        ) : visibleSources.length ? (
          <div className="application-source-table" role="table" aria-label={`${module.label} sources`}>
            <div className="application-source-table-head" role="row">
              <span>Source</span>
              <span>Stage</span>
              <span>Coverage</span>
              <span>Updated</span>
              <span>Actions</span>
            </div>
            {visibleSources.map((source) => {
              const sourceId = String(source?.id || '');
              const openPath = buildApplicationSourceOpenPath(application.id, moduleId, sourceId);
              const sectionsPath = compactCount(source?.counts?.sections) > 0
                ? buildApplicationSectionsPath(application.id, moduleId, sourceId)
                : '';
              const isConfirmingDelete = confirmDeleteId === sourceId;
              const isDeleting = deletingId === sourceId;
              const isArchiving = archivingId === sourceId;
              const isArchived = String(source?.status || '').toLowerCase() === 'archived';
              const toggleArchive = async () => {
                if (!canManage || !sourceId || typeof onArchiveSource !== 'function') return;
                setArchivingId(sourceId);
                setDeleteError('');
                try {
                  await onArchiveSource(source, !isArchived);
                } catch (err) {
                  setDeleteError(err?.message || 'Failed to archive source.');
                } finally {
                  setArchivingId('');
                }
              };
              const deleteSource = async () => {
                if (!canManage || !sourceId || typeof onDeleteSource !== 'function') return;
                if (!isConfirmingDelete) {
                  setDeleteError('');
                  setConfirmDeleteId(sourceId);
                  return;
                }
                setDeletingId(sourceId);
                setDeleteError('');
                try {
                  await onDeleteSource(source);
                  setConfirmDeleteId('');
                } catch (err) {
                  setDeleteError(err?.message || 'Failed to delete source.');
                } finally {
                  setDeletingId('');
                }
              };
              return (
                <div className="application-source-row" role="row" key={source.id}>
                  <button
                    type="button"
                    className="application-source-name"
                    aria-label={['brd', 'requirements'].includes(moduleId)
                      ? `Open ${source.title} at intake`
                      : `Open ${source.title}`}
                    onClick={() => onNavigate(openPath)}
                  >
                    <span className={`application-source-icon ${MODULE_TONES[moduleId]}`}>
                      <Icon className="h-4 w-4" />
                    </span>
                    <span>
                      <strong>
                        {source.title}
                        {isArchived && <em className="application-archived-badge">Archived</em>}
                      </strong>
                      <small>{formatStatus(source.status)}</small>
                    </span>
                  </button>
                  <span className="application-stage-chip">{formatStatus(source.current_stage)}</span>
                  <span className="application-source-coverage">
                    <strong>{compactCount(source?.counts?.test_cases)}</strong> cases
                    <small>{compactCount(source?.counts?.scripts)} scripts</small>
                  </span>
                  <span className="application-source-updated">{formatDate(source.updated_at)}</span>
                  <span className="application-source-actions">
                    <button
                      type="button"
                      className="secondary-btn"
                      onClick={() => onNavigate(openPath)}
                    >
                      {['brd', 'requirements'].includes(moduleId) ? 'Open intake' : 'Open'}
                    </button>
                    {sectionsPath && (
                      <button
                        type="button"
                        className="secondary-btn"
                        onClick={() => onNavigate(sectionsPath)}
                      >
                        Sections
                      </button>
                    )}
                    <button
                      type="button"
                      className="primary-btn"
                      onClick={() => onNavigate(source.continue_route)}
                    >
                      Continue
                      <ArrowRight className="h-4 w-4" />
                    </button>
                    {canManage && (
                      <button
                        type="button"
                        className="secondary-btn"
                        onClick={toggleArchive}
                        disabled={isArchiving || isDeleting}
                        title={isArchived
                          ? `Restore ${source.title}`
                          : `Archive ${source.title} (keeps the data and the name)`}
                        aria-label={isArchived ? `Restore ${source.title}` : `Archive ${source.title}`}
                      >
                        {isArchiving ? (
                          <LoaderCircle className="h-4 w-4 animate-spin" />
                        ) : isArchived ? (
                          <ArchiveRestore className="h-4 w-4" />
                        ) : (
                          <Archive className="h-4 w-4" />
                        )}
                      </button>
                    )}
                    {canManage && (
                      <button
                        type="button"
                        className={`application-source-delete ${isConfirmingDelete ? 'is-confirming' : ''}`}
                        onClick={deleteSource}
                        disabled={isDeleting || isArchiving}
                        title={isConfirmingDelete
                          ? `Confirm permanent delete of ${source.title}`
                          : `Delete ${source.title} permanently (frees the name)`}
                        aria-label={isConfirmingDelete
                          ? `Confirm permanent delete of ${source.title}`
                          : `Delete ${source.title} permanently`}
                      >
                        {isDeleting ? (
                          <LoaderCircle className="h-4 w-4 animate-spin" />
                        ) : (
                          <Trash2 className="h-4 w-4" />
                        )}
                        {isConfirmingDelete && <span>Confirm</span>}
                      </button>
                    )}
                  </span>
                </div>
              );
            })}
          </div>
        ) : (
          <EmptyState
            icon={Icon}
            title={query ? 'No matching sources' : `No ${module.label.toLowerCase()} sources`}
            copy={query ? 'Try a different search.' : `Create the first ${module.shortLabel.toLowerCase()} source for this application.`}
            action={!query ? (
              <button
                type="button"
                className="primary-btn"
                onClick={() => onNavigate(buildNewApplicationSourcePath(application.id, moduleId))}
              >
                <Plus className="h-4 w-4" />
                New {module.shortLabel}
              </button>
            ) : null}
          />
        )}
      </section>
      {showRecordedFlowBuilder && moduleId === 'feature' && (
        <RecordedFlowBuilder
          application={application}
          sources={sources}
          onClose={() => setShowRecordedFlowBuilder(false)}
          onCreated={(payload) => {
            setShowRecordedFlowBuilder(false);
            if (!payload?.id) return;
            onNavigate(
              `${buildApplicationModulePath(application.id, 'feature')}/${encodeURIComponent(payload.id)}/captured-flow`
            );
          }}
        />
      )}
    </>
  );
};

const SourceOverview = ({
  application,
  moduleId,
  source,
  canManage = false,
  onNavigate,
  onDeleteSource,
}) => {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const module = APPLICATION_MODULES[moduleId];
  const Icon = MODULE_ICONS[moduleId];
  if (!source) {
    return (
      <EmptyState
        icon={Icon}
        title="Source not found"
        copy="This source is no longer available in the application."
        action={(
          <button
            type="button"
            className="secondary-btn"
            onClick={() => onNavigate(buildApplicationModulePath(application.id, moduleId))}
          >
            Back to {module.label}
          </button>
        )}
      />
    );
  }
  const counts = source.counts || {};
  const openPath = buildApplicationSourceOpenPath(application.id, moduleId, source.id);
  const sectionsPath = compactCount(counts.sections) > 0
    ? buildApplicationSectionsPath(application.id, moduleId, source.id)
    : '';
  const deleteSource = async () => {
    if (!canManage || typeof onDeleteSource !== 'function') return;
    if (!confirmDelete) {
      setDeleteError('');
      setConfirmDelete(true);
      return;
    }
    setDeleting(true);
    setDeleteError('');
    try {
      await onDeleteSource(source);
    } catch (err) {
      setDeleteError(err?.message || 'Failed to delete source.');
      setDeleting(false);
    }
  };
  return (
    <>
      <header className="application-page-header">
        <div>
          <p className="application-eyebrow">{module.label}</p>
          <h1>{source.title}</h1>
          <p>Updated {formatDate(source.updated_at)}</p>
        </div>
        <div className="application-page-actions">
          <button
            type="button"
            className="secondary-btn"
            onClick={() => onNavigate(buildApplicationModulePath(application.id, moduleId))}
          >
            <ArrowLeft className="h-4 w-4" />
            Back to {module.label}
          </button>
          {openPath !== buildApplicationSourcePath(application.id, moduleId, source.id) && (
            <button type="button" className="secondary-btn" onClick={() => onNavigate(openPath)}>
              Open intake
            </button>
          )}
          {sectionsPath && (
            <button type="button" className="secondary-btn" onClick={() => onNavigate(sectionsPath)}>
              Sections
            </button>
          )}
          <button type="button" className="primary-btn" onClick={() => onNavigate(source.continue_route)}>
            Continue
            <ArrowRight className="h-4 w-4" />
          </button>
          {canManage && (
            <button
              type="button"
              className={`application-source-delete application-source-delete-lg ${confirmDelete ? 'is-confirming' : ''}`}
              onClick={deleteSource}
              disabled={deleting}
              title={confirmDelete ? `Confirm delete ${source.title}` : `Delete ${source.title}`}
              aria-label={confirmDelete ? `Confirm delete ${source.title}` : `Delete ${source.title}`}
            >
              {deleting ? (
                <LoaderCircle className="h-4 w-4 animate-spin" />
              ) : (
                <Trash2 className="h-4 w-4" />
              )}
              <span>{confirmDelete ? 'Confirm delete' : 'Delete'}</span>
            </button>
          )}
        </div>
      </header>
      {deleteError && <div className="application-error-banner mt-4">{deleteError}</div>}
      <section className="application-source-profile">
        <span className={`application-source-profile-icon ${MODULE_TONES[moduleId]}`}>
          <Icon className="h-7 w-7" />
        </span>
        <div>
          <small>Current stage</small>
          <strong>{formatStatus(source.current_stage)}</strong>
        </div>
        <div>
          <small>Status</small>
          <strong>{formatStatus(source.status)}</strong>
        </div>
        <div>
          <small>Test cases</small>
          <strong>{compactCount(counts.test_cases)}</strong>
        </div>
        <div>
          <small>POMs</small>
          <strong>{compactCount(counts.poms)}</strong>
        </div>
        <div>
          <small>Scripts</small>
          <strong>{compactCount(counts.scripts)}</strong>
        </div>
        <div>
          <small>Runs</small>
          <strong>{compactCount(counts.runs)}</strong>
        </div>
      </section>
      <section className="application-section">
        <div className="application-section-heading">
          <div>
            <p className="application-eyebrow">Activity</p>
            <h2>Source timeline</h2>
          </div>
        </div>
        <div className="application-source-timeline">
          <span><Clock3 className="h-4 w-4" /></span>
          <div>
            <strong>{formatStatus(source.current_stage)}</strong>
            <p>{formatDate(source.updated_at)}</p>
          </div>
        </div>
      </section>
    </>
  );
};

const EnvironmentDialog = ({
  applicationId,
  environment = null,
  isOpen,
  onClose,
  onSaved,
}) => {
  const [name, setName] = useState('');
  const [baseUrl, setBaseUrl] = useState('');
  const [isDefault, setIsDefault] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const isEditing = Boolean(environment?.id);

  useEffect(() => {
    if (!isOpen) return;
    setName(environment?.name || '');
    setBaseUrl(environment?.base_url || '');
    setIsDefault(Boolean(environment?.is_default));
    setError('');
  }, [environment, isOpen]);

  if (!isOpen) return null;

  const save = async (event) => {
    event.preventDefault();
    const normalizedUrl = String(baseUrl || '').trim();
    try {
      const parsed = new URL(normalizedUrl);
      if (!['http:', 'https:'].includes(parsed.protocol)) throw new Error();
    } catch (_error) {
      setError('Enter a valid HTTP or HTTPS Application URL.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const endpoint = isEditing
        ? API_ENDPOINTS.APPLICATION_ENVIRONMENT_BY_ID(applicationId, environment.id)
        : API_ENDPOINTS.APPLICATION_ENVIRONMENTS(applicationId);
      const response = await apiFetch(
        `${API_BASE_URL}${endpoint}`,
        {
          method: isEditing ? 'PATCH' : 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name,
            base_url: normalizedUrl,
            is_default: isDefault,
          }),
        }
      );
      if (!response.ok) {
        throw new Error(await parseApiError(
          response,
          isEditing ? 'Failed to update environment.' : 'Failed to add environment.'
        ));
      }
      setName('');
      setBaseUrl('');
      setIsDefault(false);
      await onSaved?.();
      onClose();
    } catch (err) {
      setError(err?.message || (
        isEditing ? 'Failed to update environment.' : 'Failed to add environment.'
      ));
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="modal-overlay" role="presentation" onClick={onClose}>
      <form
        className="modal-shell max-w-lg"
        role="dialog"
        aria-modal="true"
        aria-label={isEditing ? 'Edit application environment' : 'Add application environment'}
        onClick={(event) => event.stopPropagation()}
        onSubmit={save}
      >
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
          <div>
            <h3 className="text-lg font-semibold text-slate-900">
              {isEditing ? 'Edit Environment' : 'Add Environment'}
            </h3>
            <p className="text-sm text-slate-500">
              Set the one URL used to open this application before recording or execution.
            </p>
          </div>
          <button type="button" className="icon-btn" onClick={onClose} aria-label="Close">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="space-y-4 p-5">
          <label className="block">
            <span className="mb-1 block text-sm font-semibold text-slate-700">Name</span>
            <input className="text-input" value={name} onChange={(event) => setName(event.target.value)} placeholder="UAT" />
          </label>
          <label className="block">
            <span className="mb-1 block text-sm font-semibold text-slate-700">Application URL</span>
            <input type="url" required className="text-input" value={baseUrl} onChange={(event) => setBaseUrl(event.target.value)} placeholder="https://uat.example.com/login" />
            <span className="mt-1.5 block text-xs leading-5 text-slate-500">Use the login page when sign-in is required; otherwise use the application home page.</span>
          </label>
          <label className="flex items-center gap-2 text-sm font-semibold text-slate-700">
            <input
              type="checkbox"
              checked={isDefault}
              onChange={(event) => setIsDefault(event.target.checked)}
              disabled={Boolean(environment?.is_default)}
            />
            Default environment
          </label>
          {error && <div className="application-error-banner">{error}</div>}
          <div className="flex justify-end gap-2">
            <button type="button" className="secondary-btn" onClick={onClose}>Cancel</button>
            <button type="submit" className="primary-btn" disabled={saving || !String(name || '').trim() || !String(baseUrl || '').trim()}>
              {saving && <LoaderCircle className="h-4 w-4 animate-spin" />}
              {isEditing ? 'Save' : 'Add'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

const ApplicationSettingsDialog = ({
  application,
  isOpen,
  onClose,
  onSave,
  onArchive,
}) => {
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [confirmArchive, setConfirmArchive] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    setName(application?.name || '');
    setSaving(false);
    setArchiving(false);
    setConfirmArchive(false);
    setError('');
  }, [application?.name, isOpen]);

  if (!isOpen) return null;

  const save = async (event) => {
    event.preventDefault();
    const normalizedName = String(name || '').trim();
    if (!normalizedName || normalizedName === String(application?.name || '').trim()) {
      onClose();
      return;
    }
    setSaving(true);
    setError('');
    try {
      await onSave(normalizedName);
      onClose();
    } catch (err) {
      setError(err?.message || 'Failed to update application.');
    } finally {
      setSaving(false);
    }
  };

  const archive = async () => {
    if (!confirmArchive) {
      setConfirmArchive(true);
      return;
    }
    setArchiving(true);
    setError('');
    try {
      await onArchive();
      onClose();
    } catch (err) {
      setError(err?.message || 'Failed to archive application.');
    } finally {
      setArchiving(false);
    }
  };

  return (
    <div className="modal-overlay" role="presentation" onClick={onClose}>
      <form
        className="modal-shell max-w-lg"
        role="dialog"
        aria-modal="true"
        aria-label="Application settings"
        onClick={(event) => event.stopPropagation()}
        onSubmit={save}
      >
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
          <div>
            <h3 className="text-lg font-semibold text-slate-900">Application Settings</h3>
            <p className="text-sm text-slate-500">Manage the shared application record.</p>
          </div>
          <button type="button" className="icon-btn" onClick={onClose} aria-label="Close">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="space-y-5 p-5">
          <label className="block">
            <span className="mb-1 block text-sm font-semibold text-slate-700">Application name</span>
            <input
              className="text-input"
              value={name}
              onChange={(event) => setName(event.target.value)}
              maxLength={120}
            />
          </label>

          <div className="application-settings-danger">
            <div>
              <strong>{confirmArchive ? 'Archive this application?' : 'Archive application'}</strong>
              <p>
                {confirmArchive
                  ? 'It will be removed from every member’s active application list.'
                  : 'All sources remain stored, but the application will no longer be active.'}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {confirmArchive && (
                <button
                  type="button"
                  className="secondary-btn"
                  onClick={() => setConfirmArchive(false)}
                  disabled={archiving}
                >
                  Cancel
                </button>
              )}
              <button
                type="button"
                className="danger-btn"
                onClick={archive}
                disabled={archiving}
              >
                {archiving ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                {confirmArchive ? 'Confirm archive' : 'Archive'}
              </button>
            </div>
          </div>

          {error && <div className="application-error-banner">{error}</div>}
          <div className="flex justify-end gap-2">
            <button type="button" className="secondary-btn" onClick={onClose}>Cancel</button>
            <button
              type="submit"
              className="primary-btn"
              disabled={saving || !String(name || '').trim()}
            >
              {saving && <LoaderCircle className="h-4 w-4 animate-spin" />}
              Save
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export const ApplicationPortal = ({
  currentUser,
  catalog,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const route = useMemo(() => parseApplicationRoute(location.pathname), [location.pathname]);
  const application = catalog.applications.find(
    (item) => String(item?.id || '') === String(route.applicationId || '')
  ) || null;
  const [overview, setOverview] = useState(null);
  const [overviewLoading, setOverviewLoading] = useState(false);
  const [overviewError, setOverviewError] = useState('');
  const [sources, setSources] = useState([]);
  const [sourcesLoading, setSourcesLoading] = useState(false);
  const [showArchivedSources, setShowArchivedSources] = useState(false);
  const [sourcesError, setSourcesError] = useState('');
  const [showMembers, setShowMembers] = useState(false);
  const [members, setMembers] = useState([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [membersError, setMembersError] = useState('');
  const [activeUsers, setActiveUsers] = useState([]);
  const [activeUsersLoading, setActiveUsersLoading] = useState(false);
  const [activeUsersError, setActiveUsersError] = useState('');
  const [showEnvironment, setShowEnvironment] = useState(false);
  const [environmentToEdit, setEnvironmentToEdit] = useState(null);
  const [authEnvironment, setAuthEnvironment] = useState(null);
  const [activeEnvironmentId, setActiveEnvironmentId] = useState('');
  const [showApplicationSettings, setShowApplicationSettings] = useState(false);
  const preferenceUserId = applicationEnvironmentPreferenceUserId(currentUser);

  useEffect(() => {
    const environments = Array.isArray(overview?.environments) ? overview.environments : [];
    const applicationId = String(route.applicationId || '').trim();
    if (!applicationId || !environments.length) {
      setActiveEnvironmentId('');
      return;
    }
    const preferredId = getActiveApplicationEnvironmentId(applicationId, preferenceUserId);
    const preferred = environments.find((environment) => String(environment?.id || '') === preferredId);
    const fallback = environments.find((environment) => environment?.is_default) || environments[0];
    setActiveEnvironmentId(String((preferred || fallback)?.id || ''));
  }, [overview?.environments, preferenceUserId, route.applicationId]);

  const selectActiveEnvironment = useCallback((environmentId) => {
    const normalizedId = String(environmentId || '').trim();
    setActiveEnvironmentId(normalizedId);
    setActiveApplicationEnvironmentId(route.applicationId, normalizedId, preferenceUserId);
  }, [preferenceUserId, route.applicationId]);

  const loadOverview = useCallback(async () => {
    if (!route.applicationId) return null;
    setOverviewLoading(true);
    setOverviewError('');
    try {
      const response = await apiFetch(
        `${API_BASE_URL}${API_ENDPOINTS.APPLICATION_OVERVIEW(route.applicationId)}`
      );
      if (!response.ok) {
        throw new Error(await parseApiError(response, 'Failed to load application dashboard.'));
      }
      const payload = await response.json();
      setOverview(payload);
      return payload;
    } catch (err) {
      setOverview(null);
      setOverviewError(err?.message || 'Failed to load application dashboard.');
      return null;
    } finally {
      setOverviewLoading(false);
    }
  }, [route.applicationId]);

  const loadSources = useCallback(async (options = {}) => {
    if (!route.applicationId || !route.module) {
      setSources([]);
      return [];
    }
    const silent = Boolean(options.silent);
    if (!silent) setSourcesLoading(true);
    setSourcesError('');
    try {
      const endpoint = new URL(
        `${API_BASE_URL}${API_ENDPOINTS.APPLICATION_SOURCES(route.applicationId)}`,
        window.location.origin
      );
      endpoint.searchParams.set('type', route.module);
      if (showArchivedSources) endpoint.searchParams.set('include_archived', 'true');
      const response = await apiFetch(`${endpoint.pathname}${endpoint.search}`);
      if (!response.ok) {
        throw new Error(await parseApiError(response, 'Failed to load test sources.'));
      }
      const payload = await response.json();
      const rows = Array.isArray(payload?.sources) ? payload.sources : [];
      setSources(rows);
      return rows;
    } catch (err) {
      if (!silent) setSources([]);
      setSourcesError(err?.message || 'Failed to load test sources.');
      return [];
    } finally {
      if (!silent) setSourcesLoading(false);
    }
  }, [route.applicationId, route.module, showArchivedSources]);

  useEffect(() => {
    if (!route.applicationId || ['workflow', 'new'].includes(route.kind)) return;
    loadOverview();
  }, [loadOverview, route.applicationId, route.kind]);

  useEffect(() => {
    if (!route.module || !['module', 'source'].includes(route.kind)) return;
    loadSources();
  }, [loadSources, route.kind, route.module]);

  const loadMembers = useCallback(async () => {
    if (!route.applicationId) return;
    setMembersLoading(true);
    setMembersError('');
    try {
      const response = await apiFetch(
        `${API_BASE_URL}${API_ENDPOINTS.APPLICATION_MEMBERS(route.applicationId)}`
      );
      if (!response.ok) throw new Error(await parseApiError(response, 'Failed to load members.'));
      const payload = await response.json();
      setMembers(Array.isArray(payload?.members) ? payload.members : []);
    } catch (err) {
      setMembersError(err?.message || 'Failed to load members.');
    } finally {
      setMembersLoading(false);
    }
  }, [route.applicationId]);

  const canManageMembers = Boolean(
    application?.current_user_permissions?.includes('application:manage_members')
  );
  const canManageApplication = Boolean(
    application?.current_user_permissions?.includes('application:manage')
  );

  const loadActiveUsers = useCallback(async () => {
    if (!canManageMembers) return;
    setActiveUsersLoading(true);
    setActiveUsersError('');
    try {
      const response = await apiFetch(`${API_BASE_URL}${API_ENDPOINTS.USERS}`);
      if (!response.ok) throw new Error(await parseApiError(response, 'Failed to load users.'));
      const payload = await response.json();
      setActiveUsers(Array.isArray(payload?.users) ? payload.users : []);
    } catch (err) {
      setActiveUsersError(err?.message || 'Failed to load users.');
    } finally {
      setActiveUsersLoading(false);
    }
  }, [canManageMembers]);

  const openMembers = async () => {
    setShowMembers(true);
    await Promise.all([loadMembers(), loadActiveUsers()]);
  };

  const addMember = async (payload) => {
    const response = await apiFetch(
      `${API_BASE_URL}${API_ENDPOINTS.APPLICATION_MEMBERS(route.applicationId)}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      }
    );
    if (!response.ok) throw new Error(await parseApiError(response, 'Failed to add member.'));
    await Promise.all([loadMembers(), loadOverview(), catalog.loadApplications()]);
  };

  const updateMemberModuleAccess = async (memberUserId, moduleAccess) => {
    const response = await apiFetch(
      `${API_BASE_URL}${API_ENDPOINTS.APPLICATION_MEMBER_BY_ID(route.applicationId, memberUserId)}`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ module_access: moduleAccess }),
      }
    );
    if (!response.ok) {
      throw new Error(await parseApiError(response, 'Failed to update testing-area access.'));
    }
    await Promise.all([loadMembers(), loadOverview(), catalog.loadApplications()]);
  };

  const removeMember = async (memberUserId) => {
    const response = await apiFetch(
      `${API_BASE_URL}${API_ENDPOINTS.APPLICATION_MEMBER_BY_ID(route.applicationId, memberUserId)}`,
      { method: 'DELETE' }
    );
    if (!response.ok) throw new Error(await parseApiError(response, 'Failed to remove member.'));
    await Promise.all([loadMembers(), loadOverview(), catalog.loadApplications()]);
  };

  const saveApplicationName = async (name) => {
    await catalog.updateApplication(application.id, { name });
    await loadOverview();
  };

  const archiveApplication = async () => {
    await catalog.updateApplication(application.id, { status: 'archived' });
    navigate(buildApplicationsPath(), { replace: true });
  };

  const openNewEnvironment = () => {
    setEnvironmentToEdit(null);
    setShowEnvironment(true);
  };

  const openEnvironmentEditor = (environment) => {
    setEnvironmentToEdit(environment || null);
    setShowEnvironment(true);
  };

  const archiveEnvironment = async (environment) => {
    if (!environment?.id || environment?.is_default) return;
    const approved = window.confirm(`Archive the ${environment.name} environment?`);
    if (!approved) return;
    setOverviewError('');
    try {
      const response = await apiFetch(
        `${API_BASE_URL}${API_ENDPOINTS.APPLICATION_ENVIRONMENT_BY_ID(
          application.id,
          environment.id
        )}`,
        { method: 'DELETE' }
      );
      if (!response.ok) {
        throw new Error(await parseApiError(response, 'Failed to archive environment.'));
      }
      await loadOverview();
    } catch (err) {
      setOverviewError(err?.message || 'Failed to archive environment.');
    }
  };

  const archiveSource = async (source, archived = true) => {
    const applicationId = String(application?.id || route.applicationId || '').trim();
    const sourceId = String(source?.id || '').trim();
    if (!applicationId || !sourceId) {
      throw new Error('Source is required.');
    }
    // Archiving reuses the plain DELETE (it has always archived); restore has its own route.
    const response = archived
      ? await apiFetch(
        `${API_BASE_URL}${API_ENDPOINTS.APPLICATION_SOURCE_BY_ID(applicationId, sourceId)}`,
        { method: 'DELETE' }
      )
      : await apiFetch(
        `${API_BASE_URL}${API_ENDPOINTS.APPLICATION_SOURCE_UNARCHIVE(applicationId, sourceId)}`,
        { method: 'POST' }
      );
    if (!response.ok) {
      throw new Error(await parseApiError(
        response,
        archived ? 'Failed to archive source.' : 'Failed to restore source.',
      ));
    }
    if (archived && !showArchivedSources) {
      setSources((current) => current.filter((item) => String(item?.id || '') !== sourceId));
      setOverview((current) => removeSourceFromOverview(current, source, route.module));
      await Promise.all([loadSources({ silent: true }), loadOverview(), catalog.loadApplications()]);
      return;
    }
    await Promise.all([loadSources(), loadOverview(), catalog.loadApplications()]);
  };

  const deleteSource = async (source) => {
    const applicationId = String(application?.id || route.applicationId || '').trim();
    const sourceId = String(source?.id || '').trim();
    if (!applicationId || !sourceId) {
      throw new Error('Source is required.');
    }
    const response = await apiFetch(
      `${API_BASE_URL}${API_ENDPOINTS.APPLICATION_SOURCE_BY_ID(applicationId, sourceId)}?permanent=true`,
      { method: 'DELETE' }
    );
    if (!response.ok) {
      throw new Error(await parseApiError(response, 'Failed to delete source.'));
    }
    if (route.kind === 'source' && String(route.resourceId || '') === sourceId) {
      navigate(buildApplicationModulePath(applicationId, route.module), { replace: true });
    }
    setSources((current) => current.filter((item) => String(item?.id || '') !== sourceId));
    setOverview((current) => removeSourceFromOverview(current, source, route.module));
    await Promise.all([loadSources({ silent: true }), loadOverview(), catalog.loadApplications()]);
  };

  const syncPomSummary = useCallback(async (payload = {}) => {
    // ApplicationPomManager pages its POM list server-side, so `poms` is only
    // the current page; `total` (when present) is the true catalog count.
    const latestPoms = Array.isArray(payload?.poms) ? payload.poms : null;
    const latestTotal = Number.isFinite(payload?.total) ? payload.total : latestPoms?.length;
    if (Number.isFinite(latestTotal)) {
      setOverview((previous) => {
        if (!previous) return previous;
        const summary = previous.summary || {};
        const total = summary.total || {};
        return {
          ...previous,
          summary: {
            ...summary,
            total: {
              ...total,
              poms: latestTotal,
            },
          },
        };
      });
    }

    const shouldRefreshOverview = Boolean(payload?.refreshOverview) || !latestPoms;
    if (!shouldRefreshOverview) return;

    const refreshTasks = [loadOverview()];
    if (typeof catalog?.loadApplications === 'function') {
      refreshTasks.push(catalog.loadApplications());
    }
    await Promise.all(refreshTasks);
  }, [catalog, loadOverview]);

  const pageObjectsReturnTo = useMemo(() => {
    const params = new URLSearchParams(location.search || '');
    return sanitizeInternalReturnPath(params.get('returnTo') || params.get('return_to') || '');
  }, [location.search]);
  const pageObjectsReturnSearch = buildReturnToSearch(pageObjectsReturnTo);

  if (route.kind === 'applications') {
    return (
      <ApplicationsList
        applications={catalog.applications}
        loading={catalog.loading}
        error={catalog.error}
        canCreate={Boolean(currentUser?.application_permissions?.includes('application:create'))}
        createApplication={catalog.createApplication}
        archiveApplication={(targetApplication) => (
          catalog.updateApplication(targetApplication.id, { status: 'archived' })
        )}
        restoreApplication={(targetApplication) => (
          catalog.updateApplication(targetApplication.id, { status: 'active' })
        )}
        refresh={catalog.loadApplications}
      />
    );
  }

  if (catalog.loading && !application) {
    return <ApplicationPageSkeleton />;
  }

  if (!application) {
    return (
      <main className="applications-index-page">
        <EmptyState
          icon={Briefcase}
          title="Application not found"
          copy="It may have been archived or your access may have changed."
          action={(
            <button type="button" className="secondary-btn" onClick={() => navigate(buildApplicationsPath())}>
              <ArrowLeft className="h-4 w-4" />
              Applications
            </button>
          )}
        />
      </main>
    );
  }

  if (route.module) {
    const allowedModules = new Set(
      Array.isArray(application.current_user_module_access)
        ? application.current_user_module_access
        : APPLICATION_MODULE_ORDER
    );
    if (!allowedModules.has(route.module)) {
      return (
        <main className="applications-index-page">
          <EmptyState
            icon={ShieldCheck}
            title="Testing area not shared"
            copy="Your application access does not include this testing area."
            action={(
              <button type="button" className="secondary-btn" onClick={() => navigate(buildApplicationPath(application.id))}>
                Application overview
              </button>
            )}
          />
        </main>
      );
    }
  }

  if (['workflow', 'new'].includes(route.kind)) {
    if (route.module === 'feature') {
      return (
        <FeatureCaptureWorkspace
          currentUser={currentUser}
          applicationId={application.id}
          applicationRouteMode
        />
      );
    }
    const module = APPLICATION_MODULES[route.module];
    return (
      <PDFWorkflow
        currentUser={currentUser}
        intakeMode={module.intakeMode}
        applicationId={application.id}
      />
    );
  }

  const resolvedApplication = overview?.application || application;
  const selectedSource = route.kind === 'source'
    ? sources.find((source) => String(source?.id || '') === String(route.resourceId || ''))
    : null;
  const selectedPomName = route.kind === 'poms' ? String(route.tail?.[0] || '').trim() : '';
  const selectedPomMode = route.kind === 'poms' && route.tail?.[1] === 'edit' ? 'edit' : 'view';

  return (
    <>
      <ApplicationShell
        application={resolvedApplication}
        route={route}
        overview={overview}
        onNavigate={navigate}
        onMembers={openMembers}
      >
        {overviewError && <div className="application-error-banner">{overviewError}</div>}
        {overviewLoading && !overview ? (
          <ApplicationContentSkeleton />
        ) : route.kind === 'overview' ? (
          <ApplicationOverview
            application={resolvedApplication}
            overview={overview}
            onNavigate={navigate}
            onMembers={openMembers}
            onSettings={() => setShowApplicationSettings(true)}
            onAddEnvironment={openNewEnvironment}
            onCaptureEnvironmentLogin={setAuthEnvironment}
            onManageEnvironment={openEnvironmentEditor}
            onArchiveEnvironment={archiveEnvironment}
            activeEnvironmentId={activeEnvironmentId}
            onSelectEnvironment={selectActiveEnvironment}
            canManage={canManageApplication}
          />
        ) : route.kind === 'poms' ? (
          selectedPomName ? (
            <GeneratePOMStep
              applicationId={resolvedApplication.id}
              pomCatalogScope="application"
              initialPOMName={selectedPomName}
              initialViewMode={selectedPomMode}
              showBackButton={false}
              backLabel="Back to Page Objects"
              onBack={() => navigate(`${buildApplicationPomsPath(resolvedApplication.id)}${pageObjectsReturnSearch}`)}
              onPOMChange={syncPomSummary}
            />
          ) : (
            <ApplicationPomManager
              application={resolvedApplication}
              onPOMChange={syncPomSummary}
              returnTo={pageObjectsReturnTo}
              onClose={pageObjectsReturnTo ? () => navigate(pageObjectsReturnTo) : null}
              closeLabel={pageObjectsReturnTo ? 'Back to Workflow' : 'Close'}
            />
          )
        ) : route.kind === 'source' ? (
          <SourceOverview
            application={resolvedApplication}
            moduleId={route.module}
            source={selectedSource}
            canManage={canManageApplication}
            onNavigate={navigate}
            onDeleteSource={deleteSource}
          />
        ) : (
          <ModuleDashboard
            application={resolvedApplication}
            moduleId={route.module}
            sources={sources}
            loading={sourcesLoading}
            error={sourcesError}
            canManage={canManageApplication}
            onNavigate={navigate}
            onRefresh={() => Promise.all([loadSources(), loadOverview()])}
            onDeleteSource={deleteSource}
            onArchiveSource={archiveSource}
            showArchived={showArchivedSources}
            onToggleShowArchived={setShowArchivedSources}
          />
        )}
      </ApplicationShell>

      <WorkspaceMembersModal
        isOpen={showMembers}
        workspace={resolvedApplication}
        members={members}
        loading={membersLoading}
        error={membersError}
        activeUsers={activeUsers}
        activeUsersLoading={activeUsersLoading}
        activeUsersError={activeUsersError}
        canManageMembers={canManageMembers}
        onClose={() => setShowMembers(false)}
        onRefresh={loadMembers}
        onRefreshActiveUsers={loadActiveUsers}
        onAddMember={addMember}
        onUpdateModuleAccess={updateMemberModuleAccess}
        onRemoveMember={removeMember}
      />
      <EnvironmentDialog
        applicationId={application.id}
        environment={environmentToEdit}
        isOpen={showEnvironment}
        onClose={() => {
          setShowEnvironment(false);
          setEnvironmentToEdit(null);
        }}
        onSaved={loadOverview}
      />
      <ApplicationSettingsDialog
        application={resolvedApplication}
        isOpen={showApplicationSettings}
        onClose={() => setShowApplicationSettings(false)}
        onSave={saveApplicationName}
        onArchive={archiveApplication}
      />
      {authEnvironment?.auth_source_id && (
        <AutomationEnvironmentModal
          pdfId={authEnvironment.auth_source_id}
          defaultLoginUrl={authEnvironment.base_url || ''}
          onClose={() => setAuthEnvironment(null)}
          onSaved={loadOverview}
          onContinue={() => setAuthEnvironment(null)}
        />
      )}
    </>
  );
};
